import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Users, CurrencyDollar, ChartBar, Trash, ShieldCheck,
  Lightning, UserCircle, ArrowUp, ArrowDown,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [activeSection, setActiveSection] = useState("overview");
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, paymentsRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/payments`),
        axios.get(`${API}/admin/usage-analytics`),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setPayments(paymentsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (err) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const updateUserRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, { role: newRole });
      toast.success(`User role updated to ${newRole}`);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update role");
    }
  };

  const deleteUser = async (userId, email) => {
    if (!window.confirm(`Delete user ${email}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success("User deleted");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete user");
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center" data-testid="admin-loading">
        <p className="text-neutral-500 text-sm">Loading admin data...</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-black" data-testid="admin-dashboard">
      {/* Header */}
      <div className="border-b border-border p-6 bg-surface">
        <div className="flex items-center gap-3 mb-4">
          <ShieldCheck size={28} weight="duotone" className="text-green-400" />
          <div>
            <h2 className="text-2xl font-bold tracking-tight" data-testid="admin-header-title">Admin Dashboard</h2>
            <p className="text-sm text-muted-foreground">Platform analytics & user management</p>
          </div>
        </div>
        <div className="flex gap-2">
          {["overview", "users", "payments", "analytics"].map((section) => (
            <Button
              key={section}
              onClick={() => setActiveSection(section)}
              variant={activeSection === section ? "default" : "outline"}
              className={activeSection === section ? "bg-white text-black" : "border-border"}
              size="sm"
              data-testid={`admin-tab-${section}`}
            >
              {section.charAt(0).toUpperCase() + section.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {/* Overview */}
        {activeSection === "overview" && stats && (
          <div className="space-y-6" data-testid="admin-overview">
            {/* Stat Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard icon={Users} label="Total Users" value={stats.users.total} sub={`+${stats.users.new_this_week} this week`} color="text-blue-400" />
              <StatCard icon={CurrencyDollar} label="Revenue" value={`$${stats.revenue.total.toFixed(2)}`} sub={`${stats.revenue.total_payments} payments`} color="text-green-400" />
              <StatCard icon={ChartBar} label="Today Usage" value={stats.usage.today} sub={`${stats.usage.this_week} this week`} color="text-purple-400" />
              <StatCard icon={Lightning} label="Pro Users" value={stats.users.pro} sub={`${stats.users.free} free`} color="text-yellow-400" />
            </div>

            {/* Content Stats */}
            <div className="border border-border p-4">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-400 mb-3">Platform Content</h3>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                {Object.entries(stats.content).map(([key, val]) => (
                  <div key={key} className="text-center p-3 border border-border/50">
                    <p className="text-2xl font-bold">{val}</p>
                    <p className="text-xs text-neutral-400">{key.replace(/_/g, " ")}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Today's Usage Breakdown */}
            {Object.keys(stats.usage.today_by_action).length > 0 && (
              <div className="border border-border p-4">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-400 mb-3">Today&apos;s Usage by Action</h3>
                <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                  {Object.entries(stats.usage.today_by_action).map(([action, count]) => (
                    <div key={action} className="flex items-center justify-between p-2 border border-border/50">
                      <span className="text-xs text-neutral-400">{action.replace(/_/g, " ")}</span>
                      <span className="text-sm font-bold">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Users */}
        {activeSection === "users" && (
          <div data-testid="admin-users-section">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{users.length} Users</h3>
              <Button size="sm" variant="outline" className="border-border" onClick={loadAll} data-testid="admin-refresh-users">
                Refresh
              </Button>
            </div>
            <div className="border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-surface">
                    <th className="text-left p-3 text-xs uppercase tracking-wider text-neutral-400">User</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider text-neutral-400">Role</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider text-neutral-400">Joined</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider text-neutral-400">Today</th>
                    <th className="text-right p-3 text-xs uppercase tracking-wider text-neutral-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-border/50 hover:bg-surface/50" data-testid={`admin-user-row-${u.id}`}>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <UserCircle size={24} className="text-neutral-500" />
                          <div>
                            <p className="font-medium">{u.name || "—"}</p>
                            <p className="text-xs text-neutral-400">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-3">
                        <RoleBadge role={u.role} />
                      </td>
                      <td className="p-3 text-xs text-neutral-400">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="p-3 text-xs">{u.today_usage} actions</td>
                      <td className="p-3 text-right">
                        {u.role !== "admin" && (
                          <div className="flex items-center justify-end gap-1">
                            {u.role === "user" && (
                              <Button size="sm" variant="ghost" onClick={() => updateUserRole(u.id, "pro")}
                                className="text-yellow-400 hover:bg-yellow-400/10 text-xs h-7 px-2"
                                data-testid={`upgrade-user-${u.id}`}>
                                <ArrowUp size={14} className="mr-1" /> Pro
                              </Button>
                            )}
                            {u.role === "pro" && (
                              <Button size="sm" variant="ghost" onClick={() => updateUserRole(u.id, "user")}
                                className="text-neutral-400 hover:bg-neutral-400/10 text-xs h-7 px-2"
                                data-testid={`downgrade-user-${u.id}`}>
                                <ArrowDown size={14} className="mr-1" /> Free
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => deleteUser(u.id, u.email)}
                              className="text-red-400 hover:bg-red-400/10 text-xs h-7 px-2"
                              data-testid={`delete-user-${u.id}`}>
                              <Trash size={14} />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Payments */}
        {activeSection === "payments" && (
          <div data-testid="admin-payments-section">
            <h3 className="text-lg font-semibold mb-4">{payments.length} Transactions</h3>
            {payments.length === 0 ? (
              <div className="text-center py-12 text-neutral-500">
                <CurrencyDollar size={48} className="mx-auto mb-3 opacity-30" />
                <p>No payment transactions yet</p>
              </div>
            ) : (
              <div className="border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-surface">
                      <th className="text-left p-3 text-xs uppercase tracking-wider text-neutral-400">Email</th>
                      <th className="text-left p-3 text-xs uppercase tracking-wider text-neutral-400">Amount</th>
                      <th className="text-left p-3 text-xs uppercase tracking-wider text-neutral-400">Status</th>
                      <th className="text-left p-3 text-xs uppercase tracking-wider text-neutral-400">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p) => (
                      <tr key={p.session_id || p.created_at} className="border-b border-border/50">
                        <td className="p-3">{p.email}</td>
                        <td className="p-3 font-medium">${p.amount}</td>
                        <td className="p-3">
                          <span className={`text-xs px-2 py-1 border ${getPaymentStatusStyle(p.payment_status)}`}>
                            {p.payment_status}
                          </span>
                        </td>
                        <td className="p-3 text-xs text-neutral-400">
                          {p.created_at ? new Date(p.created_at).toLocaleString() : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Analytics */}
        {activeSection === "analytics" && analytics && (
          <div className="space-y-6" data-testid="admin-analytics-section">
            {/* Daily Usage Chart */}
            <div className="border border-border p-4">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-400 mb-4">Last 7 Days Usage</h3>
              <div className="flex items-end gap-2 h-40">
                {analytics.daily_usage.map((day) => {
                  const maxCount = Math.max(...analytics.daily_usage.map((d) => d.count), 1);
                  const height = Math.max((day.count / maxCount) * 100, 4);
                  return (
                    <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-xs text-neutral-400">{day.count}</span>
                      <div
                        className="w-full bg-blue-500/80 transition-all"
                        style={{ height: `${height}%` }}
                        data-testid={`usage-bar-${day.date}`}
                      />
                      <span className="text-xs text-neutral-500">{day.day}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Top Users */}
            <div className="border border-border p-4">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-400 mb-3">Top Users (This Week)</h3>
              {analytics.top_users.length === 0 ? (
                <p className="text-sm text-neutral-500">No usage data yet</p>
              ) : (
                <div className="space-y-2">
                  {analytics.top_users.map((u) => (
                    <div key={u.email} className="flex items-center justify-between p-2 border border-border/50">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-neutral-500 w-5">#{analytics.top_users.indexOf(u) + 1}</span>
                        <span className="text-sm">{u.email}</span>
                        <RoleBadge role={u.role} />
                      </div>
                      <span className="text-sm font-bold">{u.usage_count} actions</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="border border-border p-4" data-testid={`stat-card-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={20} className={color} />
        <span className="text-xs uppercase tracking-wider text-neutral-400">{label}</span>
      </div>
      <p className="text-2xl font-bold">{value}</p>
      {sub && <p className="text-xs text-neutral-500 mt-1">{sub}</p>}
    </motion.div>
  );
}

function getPaymentStatusStyle(status) {
  if (status === "paid") return "text-green-400 border-green-800";
  if (status === "initiated") return "text-yellow-400 border-yellow-800";
  return "text-neutral-400 border-neutral-700";
}

function RoleBadge({ role }) {
  const styles = {
    admin: "text-green-400 border-green-800",
    pro: "text-yellow-400 border-yellow-800",
    user: "text-neutral-400 border-neutral-700",
  };
  return (
    <span className={`text-xs px-2 py-0.5 border ${styles[role] || styles.user}`}>
      {role}
    </span>
  );
}
